import type { ReportRequest } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  FieldSet,
  SelectButton,
  SelectButtons,
  Input,
  Label,
  Legend,
  InfoButton,
} from "@tandemdiabetes/sugarmate-react-components"
import dayjs from "dayjs"
import type { ChangeEventHandler } from "react"
import {
  useCallback,
  useState,
  useEffect,
  useMemo,
} from "react"
import styles from "./Reports.module.scss"
import { usePolyglot } from "src/contexts"
import { useToday } from "src/hooks"

const polyglotPrefix = "pages.settings.generate_report."

enum ReportDateRange {
  ThirtyDays = "thirty_days",
  NinetyDays = "ninety_days",
  Custom = "custom",
}

const dateInputFormat = "YYYY-MM-DD"

interface Props {
  reportRequest: ReportRequest;
  updateReportRequest: (reportRequest: Partial<ReportRequest>) => void;
}

export function DateRangeInput(props: Props) {
  const {
    updateReportRequest,
    reportRequest,
  } = props

  const polyglot = usePolyglot()
  const today = useToday()

  const [
    currentDateRange,
    setCurrentDateRange,
  ] = useState<ReportDateRange>(ReportDateRange.ThirtyDays)

  useEffect(
    () => {
      if (currentDateRange === ReportDateRange.Custom) {
        return
      }

      const endDate = dayjs(today).endOf("day")
      const startDate = dayjs(today).subtract(
        currentDateRange === ReportDateRange.ThirtyDays ? 30 : 90,
        "days",
      )
        .startOf("day")

      updateReportRequest({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
      })
    },
    [
      today,
      currentDateRange,
      updateReportRequest,
    ],
  )

  const onDateRangeChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const newDateRange = e.target.value as ReportDateRange
      setCurrentDateRange(newDateRange)
    },
    [setCurrentDateRange],
  )

  const onStartDateChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const newStartDate = dayjs(
        e.target.value,
        dateInputFormat,
      ).startOf("day")
      updateReportRequest({
        start_date: newStartDate.toISOString(),
      })
    },
    [updateReportRequest],
  )

  const onEndDateChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const newEndDate = dayjs(
        e.target.value,
        dateInputFormat,
      ).endOf("day")
      updateReportRequest({
        end_date: newEndDate.toISOString(),
      })
    },
    [updateReportRequest],
  )

  const {
    maxDate,
    minDate,
  } = useMemo(
    () => {
      const max = dayjs(today).format(dateInputFormat)
      const min = dayjs(today)
        .subtract(
          90,
          "days",
        )
        .format(dateInputFormat)

      return {
        maxDate: max,
        minDate: min,
      }
    },
    [today],
  )

  return (
    <>
      <FieldSet>
        <Legend id="date-range">
          {polyglot.t(`${polyglotPrefix}date_range.legend`)}
          <InfoButton
            title={polyglot.t(`${polyglotPrefix}date_range.info_title`)}
          >
            {polyglot.t(`${polyglotPrefix}date_range.info_description`)}
          </InfoButton>
        </Legend>
        <SelectButtons className={styles.inputContainer}>
          <SelectButton
            id="date-range-option-thirty-days"
            htmlFor="date-range-thirty-days"
            name="date-range"
            value={ReportDateRange.ThirtyDays}
            onChange={onDateRangeChange}
            checked={currentDateRange === ReportDateRange.ThirtyDays}
            required
          >
            {polyglot.t(
              "time.day",
              30,
            )}
          </SelectButton>
          <SelectButton
            id="date-range-option-ninety-days"
            htmlFor="date-range-ninety-days"
            name="date-range"
            value={ReportDateRange.NinetyDays}
            onChange={onDateRangeChange}
            checked={currentDateRange === ReportDateRange.NinetyDays}
            required
          >
            {polyglot.t(
              "time.day",
              90,
            )}
          </SelectButton>
          <SelectButton
            id="date-range-option-custom"
            htmlFor="date-range-custom"
            name="date-range"
            value={ReportDateRange.Custom}
            onChange={onDateRangeChange}
            checked={currentDateRange === ReportDateRange.Custom}
            required
          >
            {polyglot.t(`${polyglotPrefix}date_range.custom_cta`)}
          </SelectButton>
        </SelectButtons>
      </FieldSet>
      {currentDateRange === ReportDateRange.Custom && (
        <>
          <div>
            <Label>
              {polyglot.t(`${polyglotPrefix}custom_date_range.label`)}
              <InfoButton
                title={polyglot.t(
                  `${polyglotPrefix}custom_date_range.info_title`,
                )}
              >
                {polyglot.t(
                  `${polyglotPrefix}custom_date_range.info_description`,
                )}
              </InfoButton>
            </Label>
            <div className={styles.inputContainer}>
              <Input
                className={styles.customDateInput}
                type="date"
                id="start-date"
                min={minDate}
                max={maxDate}
                value={dayjs(reportRequest.start_date).format(dateInputFormat)}
                onChange={onStartDateChange}
                required
              />
              <div className={styles.customDateInputSeparator}>
                –
              </div>
              <Input
                className={styles.customDateInput}
                type="date"
                id="end-date"
                min={minDate}
                max={maxDate}
                value={dayjs(reportRequest.end_date).format(dateInputFormat)}
                onChange={onEndDateChange}
                required
              />
            </div>
          </div>
        </>
      )}
    </>
  )
}
